import {
  IBookingCartPreviewResponseData,
  IBookingCommentsAlternativeCouponResponseData,
  IBookingCommentsCouponResponseData,
  IBookingMembershipListResponse,
  IBookingPurchaseOptionsResponseData,
  IBookingSummaryResponseData,
  IAtHomeSlotBucket,
  IBookingShiftAppointmentData,
  IBookingSlotsClinicData,
  IBookingSlotsDoctorInfo,
  IBookingSlotsFilterBody,
  IBookingSlotsResponseData,
  IBookingsSlotsData,
  IOverBookingHairCutToBathServicesData,
  IOverBookingListResponseData,
  IOverBookingPermisionListResponseData,
  IOverBookingUserListResponseData,
  IUserBookingsListData,
  IUserBookingsListResponseData,
  IoverBookingHairCutGroomerResponseData,
} from "@/api_framework/api_modals/Bookings";
import { IClinicListResponseData } from "@/api_framework/api_modals/Clinics";
import { IOrgCityListResponseData, IOrgProviderListResponseData } from "@/api_framework/api_modals/Org";
import { IBookingCommentsFormFields } from "@/app/components/routes/auth_routes/SlotsScreen/CommentSection";
import { PayloadAction, createSlice, current } from "@reduxjs/toolkit";
import { IRouteReducerSliceStateInit } from "../slice_interface";

export type SlotsStatekeys =
  | "filters"
  | "bookingConfirmed"
  | "slotsList"
  | "atHomeSlotBuckets"
  | "selectedSlotsClinicDetails"
  | "selectedSlotDetails"
  | "selectedSlotStaffDetails"
  | "atHomeSelectedDoctorId"
  | "commentsData"
  | "couponsData"
  | "membershipData"
  | "purchaseOptionsData"
  | "rescheduleBookingSummaryData"
  | "bookingCartPreviewData"
  | "selectedPurchaseServiceId"
  | "selectedPurchaseAddOnIds"
  | "selectedPurchaseVaccineIds"
  | "selectedVaccineCouponMap"
  | "selectedAddOnCouponMap"
  | "selectedPurchaseMembershipId"
  | "selectedPurchaseMembershipPayAtClinic"
  | "selectedSurgeryIds"
  | "selectedSurgeryCouponMap"
  | "selectedPurchaseDiagnosticIds"
  | "selectedPurchaseOtherTestIds"
  | "rescheduleFlag"
  | "lateBookingStartTime"
  | "overbookingFlag"
  | "lateBookingFlag"
  | "rescheduledBooking"
  | "overBooking"
  | "selectedOverbooking"
  | "customerBookings"
  | "alternativeCouponsData"
  | "bdeUserList"
  | "haircutGroomer"
  | "overbookingPermisionList"
  | "haircutServices"
  | "bookedSlots"
  | "clinics"
  | "cities"
  | "providers"
  | "shiftAppoitnemt"
  | "rescheduleBookingIdForMissingInfo"
  | "currentFormServiceId"
  | "atHomePreviousFlow"
  | "isAhsRepeatUser"
  | "ahsSubServiceId"
  | "ahsSubServiceName";

export interface ISelectedSurgeryCoupon {
  couponId: string;
  couponName?: string;
}

export interface ISlotsState {
  isPageReadonly?: boolean;
  filters?: IBookingSlotsFilterBody | null;
  disableSlotsFilter?: boolean;
  bookingConfirmed?: boolean;
  slotsList?: IBookingSlotsResponseData[] | null;
  atHomeSlotBuckets?: IAtHomeSlotBucket[] | null;
  atHomePreviousFlow?: boolean;
  isAhsRepeatUser?: boolean;
  ahsSubServiceId?: string | null;
  ahsSubServiceName?: string | null;
  bookedSlots?: IBookingSlotsResponseData[] | null;
  selectedSlotsClinicDetails?: IBookingSlotsClinicData | null;
  selectedSlotDetails?: IBookingsSlotsData | null;
  selectedSlotStaffDetails?: IBookingSlotsDoctorInfo | null;
  /* Staff id of the at-home doctor chip in use; null for "best available" / no selection */
  atHomeSelectedDoctorId?: string | null;
  commentsData?: IBookingCommentsFormFields | null;
  couponsData?: IBookingCommentsCouponResponseData[] | null;
  membershipData?: IBookingMembershipListResponse | null;
  purchaseOptionsData?: IBookingPurchaseOptionsResponseData | null;
  rescheduleBookingSummaryData?: IBookingSummaryResponseData | null;
  bookingCartPreviewData?: IBookingCartPreviewResponseData | null;
  selectedPurchaseServiceId?: string | null;
  selectedPurchaseAddOnIds?: string[] | null;
  selectedPurchaseVaccineIds?: string[] | null;
  selectedVaccineCouponMap?: Record<string, string> | null;
  selectedAddOnCouponMap?: Record<string, string> | null;
  selectedPurchaseMembershipId?: string | null;
  selectedPurchaseMembershipPayAtClinic?: boolean;
  selectedMembershipCouponId?: string;
  selectedMembershipCouponName?: string;
  selectedSurgeryIds?: string[];
  selectedSurgeryCouponMap?: Record<string, ISelectedSurgeryCoupon>;
  selectedPurchaseDiagnosticIds?: string[];
  selectedPurchaseOtherTestIds?: string[];
  rescheduleFlag?: boolean;
  rescheduleBookingIdForMissingInfo?: string | null;
  overbookingFlag?: boolean;
  lateBookingFlag?: boolean;
  lateBookingStartTime?: string;
  rescheduledBooking?: IUserBookingsListData | null;
  overBooking?: IOverBookingListResponseData[] | null;
  selectedOverbooking?: IOverBookingListResponseData;
  customerBookings?: IUserBookingsListResponseData | null;
  alternativeCouponsData?: IBookingCommentsAlternativeCouponResponseData[] | null;
  bdeUserList?: IOverBookingUserListResponseData[] | null;
  haircutGroomer?: IoverBookingHairCutGroomerResponseData[] | null;
  overbookingPermisionList?: IOverBookingPermisionListResponseData[] | null;
  haircutServices?: IOverBookingHairCutToBathServicesData[] | null;
  /* Cities Clinics & Providers */
  clinics?: IClinicListResponseData[] | null;
  cities?: IOrgCityListResponseData[] | null;
  providers?: IOrgProviderListResponseData[] | null;
  shiftAppoitnemt?: IBookingShiftAppointmentData[] | null;
  address?: any[] | null;
  currentFormServiceId?: string | null;
}

export interface ISlotsInitialState extends IRouteReducerSliceStateInit {
  state: ISlotsState;
}
const initialState: ISlotsInitialState = {
  state: {
    clinics: [],
    isPageReadonly: false,
    cities: [],
    disableSlotsFilter: false,
    providers: [],
    selectedPurchaseAddOnIds: [],
    selectedPurchaseVaccineIds: [],
    selectedVaccineCouponMap: {},
    selectedAddOnCouponMap: {},
    selectedPurchaseMembershipId: null,
    selectedPurchaseMembershipPayAtClinic: false,
    selectedMembershipCouponId: undefined,
    selectedMembershipCouponName: undefined,
    selectedSurgeryIds: [],
    selectedSurgeryCouponMap: {},
    selectedPurchaseDiagnosticIds: [],
    selectedPurchaseOtherTestIds: [],
    selectedPurchaseServiceId: null,
    purchaseOptionsData: null,
    rescheduleBookingSummaryData: null,
    bookingCartPreviewData: null,
    address: null,
    atHomeSelectedDoctorId: null,
    atHomePreviousFlow: false,
    isAhsRepeatUser: false,
    ahsSubServiceId: null,
    ahsSubServiceName: null,
  },
};

const slotsSlice = createSlice({
  name: "slots_slice",
  initialState: initialState,
  reducers: {
    RESET_SLOTS_SLICE: () => {
      return initialState;
    },
    setSlotsPageReadonly: (state, action: PayloadAction<boolean>) => {
      state.state.isPageReadonly = action.payload;
    },
    toggleSlotsFilters: (state, action: PayloadAction<{ status: boolean }>) => {
      state.state.disableSlotsFilter = action.payload.status;
    },
    setSlotsOrgCities: (
      state,
      action: PayloadAction<{
        data: IOrgCityListResponseData[];
      }>,
    ) => {
      state.state.cities = action.payload.data;
    },
    setSlotsOrgProviders: (
      state,
      action: PayloadAction<{
        data: IOrgProviderListResponseData[];
      }>,
    ) => {
      state.state.providers = action.payload.data;
    },
    setSlotsOrgClinics: (
      state,
      action: PayloadAction<{
        data: IClinicListResponseData[];
      }>,
    ) => {
      state.state.clinics = action.payload.data;
    },
    setUserHaircutServices: (
      state,
      action: PayloadAction<{
        data: IOverBookingHairCutToBathServicesData[];
      }>,
    ) => {
      state.state.haircutServices = action.payload.data;
    },
    setUserListData: (
      state,
      action: PayloadAction<{
        data: IOverBookingUserListResponseData[];
      }>,
    ) => {
      state.state.bdeUserList = action.payload.data;
    },
    setOverBookingPermisionList: (
      state,
      action: PayloadAction<{
        data: IOverBookingPermisionListResponseData[];
      }>,
    ) => {
      state.state.overbookingPermisionList = action.payload.data;
    },
    setBookingGroomerList: (
      state,
      action: PayloadAction<{
        data: IoverBookingHairCutGroomerResponseData[];
      }>,
    ) => {
      state.state.haircutGroomer = action.payload.data;
    },
    setOverBookingList: (
      state,
      action: PayloadAction<{
        data: IOverBookingListResponseData[];
      }>,
    ) => {
      state.state.overBooking = action.payload.data;
    },
    setSlotBookingFilters: (state, action: PayloadAction<{ filters: any }>) => {
      state.state.filters = action.payload.filters;
    },
    setCurrentFormServiceId: (state, action: PayloadAction<string | null>) => {
      state.state.currentFormServiceId = action.payload;
    },
    setUserAddress: (state, action: PayloadAction<{ address: any }>) => {
      state.state.address = action.payload.address;
    },
    setSelectedSlotsClinicDetails: (
      state,
      action: PayloadAction<{
        data: IBookingSlotsClinicData;
      }>,
    ) => {
      state.state.selectedSlotsClinicDetails = action.payload.data;
    },
    setSelectedSlotDetails: (state, action: PayloadAction<{ data: IBookingsSlotsData }>) => {
      state.state.selectedSlotDetails = action.payload.data;
    },
    setSelectedSlotStaffDetails: (state, action: PayloadAction<{ data: IBookingSlotsDoctorInfo }>) => {
      state.state.selectedSlotStaffDetails = action.payload.data;
    },
    setAtHomeSelectedDoctorId: (state, action: PayloadAction<string | null>) => {
      state.state.atHomeSelectedDoctorId = action.payload;
    },
    setAtHomePreviousFlow: (state, action: PayloadAction<boolean>) => {
      state.state.atHomePreviousFlow = action.payload;
    },
    setIsAhsRepeatUser: (state, action: PayloadAction<boolean>) => {
      state.state.isAhsRepeatUser = action.payload;
    },
    setAhsSubServiceId: (state, action: PayloadAction<string | null>) => {
      state.state.ahsSubServiceId = action.payload;
    },
    setAhsSubServiceName: (state, action: PayloadAction<string | null>) => {
      state.state.ahsSubServiceName = action.payload;
    },
    setSelectedOverBooking: (state, action: PayloadAction<{ data: IOverBookingListResponseData }>) => {
      state.state.selectedOverbooking = action.payload.data;
    },
    setSlotsList: (
      state,
      action: PayloadAction<{
        data: IBookingSlotsResponseData[];
      }>,
    ) => {
      state.state.slotsList = action.payload.data;
    },
    setAtHomeSlotBuckets: (
      state,
      action: PayloadAction<{
        data: IAtHomeSlotBucket[];
      }>,
    ) => {
      state.state.atHomeSlotBuckets = action.payload.data;
    },
    setSlotsComments: (state, action: PayloadAction<{ data: IBookingCommentsFormFields }>) => {
      state.state.commentsData = action.payload.data;
    },
    setAlternativeCouponData: (
      state,
      action: PayloadAction<{
        data: IBookingCommentsAlternativeCouponResponseData[];
      }>,
    ) => {
      state.state.alternativeCouponsData = action.payload.data;
    },
    setShiftAppoitnemt: (
      state,
      action: PayloadAction<{
        data: IBookingShiftAppointmentData[];
      }>,
    ) => {
      state.state.shiftAppoitnemt = action.payload.data;
    },
    setOverbookingFlag: (state, action: PayloadAction<boolean>) => {
      state.state.overbookingFlag = action.payload;
    },
    setLateBookingFlag: (state, action: PayloadAction<boolean>) => {
      state.state.lateBookingFlag = action.payload;
    },
    setDiscountCouponData: (
      state,
      action: PayloadAction<{
        data: IBookingCommentsCouponResponseData[];
      }>,
    ) => {
      state.state.couponsData = action.payload.data;
    },
    setRescheduledBooking: (state, action: PayloadAction<{ data: IUserBookingsListData }>) => {
      state.state.rescheduledBooking = action.payload.data;
    },
    setRescheduleFlag: (state, action: PayloadAction<boolean>) => {
      state.state.rescheduleFlag = action.payload;
    },
    setRescheduleBookingIdForMissingInfo: (state, action: PayloadAction<string | null>) => {
      state.state.rescheduleBookingIdForMissingInfo = action.payload;
    },
    setBookingConfirmed: (state, action: PayloadAction<boolean>) => {
      state.state.bookingConfirmed = action.payload;
    },
    setLateBookingStartTime: (state, action: PayloadAction<string>) => {
      state.state.lateBookingStartTime = action.payload;
    },
    setMembershipData: (
      state,
      action: PayloadAction<{
        data: IBookingMembershipListResponse;
      }>,
    ) => {
      state.state.membershipData = action.payload.data;
    },
    setPurchaseOptionsData: (
      state,
      action: PayloadAction<{
        data: IBookingPurchaseOptionsResponseData | null;
      }>,
    ) => {
      state.state.purchaseOptionsData = action.payload.data;
    },
    setRescheduleBookingSummaryData: (
      state,
      action: PayloadAction<{
        data: IBookingSummaryResponseData | null;
      }>,
    ) => {
      state.state.rescheduleBookingSummaryData = action.payload.data;
    },
    setBookingCartPreviewData: (
      state,
      action: PayloadAction<{
        data: IBookingCartPreviewResponseData | null;
      }>,
    ) => {
      state.state.bookingCartPreviewData = action.payload.data;
    },
    setSelectedPurchaseServiceId: (state, action: PayloadAction<string | null>) => {
      state.state.selectedPurchaseServiceId = action.payload;
    },
    setSelectedPurchaseAddOnIds: (
      state,
      action: PayloadAction<{
        data: string[];
      }>,
    ) => {
      state.state.selectedPurchaseAddOnIds = action.payload.data;
    },
    toggleSelectedPurchaseAddOnId: (state, action: PayloadAction<string>) => {
      const selectedIds = state.state.selectedPurchaseAddOnIds ?? [];
      const addOnId = action.payload;
      if (selectedIds.includes(addOnId)) {
        state.state.selectedPurchaseAddOnIds = selectedIds.filter((id) => id !== addOnId);
      } else {
        state.state.selectedPurchaseAddOnIds = [...selectedIds, addOnId];
      }
    },
    setSelectedPurchaseVaccineIds: (
      state,
      action: PayloadAction<{
        data: string[];
      }>,
    ) => {
      state.state.selectedPurchaseVaccineIds = action.payload.data;
    },
    toggleSelectedPurchaseVaccineId: (state, action: PayloadAction<string>) => {
      const selectedIds = state.state.selectedPurchaseVaccineIds ?? [];
      const vaccineId = action.payload;
      if (selectedIds.includes(vaccineId)) {
        state.state.selectedPurchaseVaccineIds = selectedIds.filter((id) => id !== vaccineId);
      } else {
        state.state.selectedPurchaseVaccineIds = [...selectedIds, vaccineId];
      }
    },
    setSelectedVaccineCoupon: (
      state,
      action: PayloadAction<{
        vaccineId: string;
        couponId: string | null;
      }>,
    ) => {
      const couponMap = { ...(state.state.selectedVaccineCouponMap ?? {}) };
      const { vaccineId, couponId } = action.payload;
      if (couponId) {
        couponMap[vaccineId] = couponId;
      } else {
        delete couponMap[vaccineId];
      }
      state.state.selectedVaccineCouponMap = couponMap;
    },
    setSelectedVaccineCouponMap: (
      state,
      action: PayloadAction<{
        data: Record<string, string>;
      }>,
    ) => {
      state.state.selectedVaccineCouponMap = action.payload.data;
    },
    setSelectedAddOnCoupon: (
      state,
      action: PayloadAction<{
        addOnId: string;
        couponId: string | null;
      }>,
    ) => {
      const couponMap = { ...(state.state.selectedAddOnCouponMap ?? {}) };
      const { addOnId, couponId } = action.payload;
      if (couponId) {
        couponMap[addOnId] = couponId;
      } else {
        delete couponMap[addOnId];
      }
      state.state.selectedAddOnCouponMap = couponMap;
    },
    setSelectedAddOnCouponMap: (
      state,
      action: PayloadAction<{
        data: Record<string, string>;
      }>,
    ) => {
      state.state.selectedAddOnCouponMap = action.payload.data;
    },
    setSelectedPurchaseMembershipId: (state, action: PayloadAction<string | null>) => {
      state.state.selectedPurchaseMembershipId = action.payload;
      if (!action.payload) {
        state.state.selectedPurchaseMembershipPayAtClinic = false;
        state.state.selectedMembershipCouponId = undefined;
        state.state.selectedMembershipCouponName = undefined;
      }
    },
    setSelectedPurchaseMembershipPayAtClinic: (state, action: PayloadAction<boolean>) => {
      state.state.selectedPurchaseMembershipPayAtClinic = action.payload;
    },
    setSelectedMembershipCouponId: (state, action: PayloadAction<string | undefined>) => {
      state.state.selectedMembershipCouponId = action.payload;
    },
    setSelectedMembershipCouponName: (state, action: PayloadAction<string | undefined>) => {
      state.state.selectedMembershipCouponName = action.payload;
    },
    setSelectedSurgeryIds: (state, action: PayloadAction<string[]>) => {
      state.state.selectedSurgeryIds = action.payload;
      const couponMap = state.state.selectedSurgeryCouponMap ?? {};
      state.state.selectedSurgeryCouponMap = Object.fromEntries(
        Object.entries(couponMap).filter(([surgeryId]) => action.payload.includes(surgeryId)),
      );
    },
    setSelectedSurgeryCoupon: (
      state,
      action: PayloadAction<{
        surgeryId: string;
        couponId: string | null;
        couponName?: string | null;
      }>,
    ) => {
      const couponMap = { ...(state.state.selectedSurgeryCouponMap ?? {}) };
      const { surgeryId, couponId, couponName } = action.payload;
      if (couponId) {
        couponMap[surgeryId] = { couponId, ...(couponName ? { couponName } : {}) };
      } else {
        delete couponMap[surgeryId];
      }
      state.state.selectedSurgeryCouponMap = couponMap;
    },
    setSelectedPurchaseDiagnosticIds: (state, action: PayloadAction<string[]>) => {
      state.state.selectedPurchaseDiagnosticIds = action.payload;
    },
    setSelectedPurchaseOtherTestIds: (state, action: PayloadAction<string[]>) => {
      state.state.selectedPurchaseOtherTestIds = action.payload;
    },
    appendBookingList: (
      state,
      action: PayloadAction<{
        data: IUserBookingsListResponseData;
      }>,
    ) => {
      const prevStateData = state.state.customerBookings as IUserBookingsListResponseData;

      state.state.customerBookings = {
        count: action.payload.data.count,
        data: [...current(prevStateData.data), ...action.payload.data.data],
      } as IUserBookingsListResponseData;
    },
    setUserBookingList: (
      state,
      action: PayloadAction<{
        data: IUserBookingsListResponseData;
      }>,
    ) => {
      state.state.customerBookings = action.payload.data;
    },
    setBookedSlotsList: (
      state,
      action: PayloadAction<{
        data: IBookingSlotsResponseData[];
      }>,
    ) => {
      state.state.bookedSlots = action.payload.data;
    },
  },
});

export const {
  appendBookingList,
  setAlternativeCouponData,
  setBookingConfirmed,
  setBookingGroomerList,
  setDiscountCouponData,
  setMembershipData,
  setPurchaseOptionsData,
  setRescheduleBookingSummaryData,
  setBookingCartPreviewData,
  setSelectedPurchaseServiceId,
  setSelectedPurchaseAddOnIds,
  toggleSelectedPurchaseAddOnId,
  setSelectedPurchaseVaccineIds,
  toggleSelectedPurchaseVaccineId,
  setSelectedVaccineCoupon,
  setSelectedVaccineCouponMap,
  setSelectedAddOnCoupon,
  setSelectedAddOnCouponMap,
  setSelectedPurchaseMembershipId,
  setSelectedPurchaseMembershipPayAtClinic,
  setSelectedMembershipCouponId,
  setSelectedMembershipCouponName,
  setSelectedSurgeryIds,
  setSelectedSurgeryCoupon,
  setSelectedPurchaseDiagnosticIds,
  setSelectedPurchaseOtherTestIds,
  setOverBookingList,
  setOverBookingPermisionList,
  setOverbookingFlag,
  setRescheduleFlag,
  setRescheduledBooking,
  setSelectedOverBooking,
  setSelectedSlotDetails,
  setSelectedSlotStaffDetails,
  setAtHomeSelectedDoctorId,
  setAtHomePreviousFlow,
  setIsAhsRepeatUser,
  setAhsSubServiceId,
  setAhsSubServiceName,
  setSelectedSlotsClinicDetails,
  setSlotBookingFilters,
  setCurrentFormServiceId,
  setSlotsComments,
  setSlotsList,
  setAtHomeSlotBuckets,
  setUserBookingList,
  setUserHaircutServices,
  setUserListData,
  setBookedSlotsList,
  setSlotsOrgCities,
  setSlotsOrgProviders,
  toggleSlotsFilters,
  setSlotsOrgClinics,
  setShiftAppoitnemt,
  setSlotsPageReadonly,
  setLateBookingFlag,
  setLateBookingStartTime,
  RESET_SLOTS_SLICE,
  setUserAddress,
  setRescheduleBookingIdForMissingInfo,
} = slotsSlice.actions;
export default slotsSlice.reducer;
